import type { DragEndEvent } from "@dnd-kit/core";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { t } from "@lingui/macro";
import { CaretDownIcon, DotsSixVerticalIcon, PlusIcon, XIcon } from "@phosphor-icons/react";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";

/**
 * SkillsForm — the tag/chip pattern with optional proficiency (builder v2).
 * Serves Skills, Interests, Languages — set `showLevels` off for plain tags.
 *
 * Fully controlled. The new-tag input is controlled via `draftValue` /
 * `onDraftChange`; committing calls `onAddSkill`. Drag-to-reorder is
 * presentational — spread `getDragHandleProps(id)` onto each chip's handle.
 */
export type Skill = {
  id: string;
  name: string;
  /** Index into `levelLabels` (0-based). Omit when `showLevels` is off. */
  level?: number;
};

export type SkillsFormProps = {
  skills: Skill[];
  draftValue: string;
  onDraftChange: (value: string) => void;
  onAddSkill?: () => void;
  onRemoveSkill?: (id: string) => void;
  onChangeLevel?: (id: string, level: number) => void;
  onReorderSkills?: (newSkills: Skill[]) => void;
  showLevels?: boolean;
  /** Defaults to Beginner / Intermediate / Advanced / Expert. */
  levelLabels?: string[];
  placeholder?: string;
};

type SortableSkillProps = {
  skill: Skill;
  levels: string[];
  showLevels: boolean;
  onChangeLevel?: (id: string, level: number) => void;
  onRemoveSkill?: (id: string) => void;
};

const SortableSkill = ({
  skill,
  levels,
  showLevels,
  onChangeLevel,
  onRemoveSkill,
}: SortableSkillProps) => {
  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({
    id: skill.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.5 : undefined,
    transition,
  };

  const skillName = skill.name;

  return (
    <span
      ref={setNodeRef}
      style={style}
      className="group inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/60 py-1 pl-1.5 pr-1 text-sm"
    >
      <button
        type="button"
        aria-label={t`Drag to reorder`}
        className="flex cursor-grab items-center text-zinc-400 hover:text-foreground active:cursor-grabbing"
        {...listeners}
        {...attributes}
      >
        <DotsSixVerticalIcon size={14} />
      </button>

      <span className="font-medium text-foreground">{skillName}</span>

      {showLevels ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-0.5 rounded-full bg-primary/10 px-1.5 py-0.5 font-mono text-[10px] text-primary"
              aria-label={t`Set level for ${skillName}`}
            >
              {levels[skill.level ?? 0]}
              <CaretDownIcon size={10} />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-44 p-1">
            {levels.map((label, index) => (
              <button
                key={label}
                type="button"
                className={cn(
                  "flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm hover:bg-accent",
                  (skill.level ?? 0) === index ? "text-primary" : "text-foreground",
                )}
                onClick={() => onChangeLevel?.(skill.id, index)}
              >
                {label}
                {(skill.level ?? 0) === index ? (
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                ) : null}
              </button>
            ))}
          </PopoverContent>
        </Popover>
      ) : null}

      <button
        type="button"
        className="flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground hover:bg-rose-500/10 hover:text-rose-500"
        aria-label={t`Remove ${skillName}`}
        onClick={() => onRemoveSkill?.(skill.id)}
      >
        <XIcon size={12} />
      </button>
    </span>
  );
};

export const SkillsForm = ({
  skills,
  draftValue,
  onDraftChange,
  onAddSkill,
  onRemoveSkill,
  onChangeLevel,
  onReorderSkills,
  showLevels = true,
  levelLabels,
  placeholder,
}: SkillsFormProps) => {
  const levels = levelLabels ?? [t`Beginner`, t`Intermediate`, t`Advanced`, t`Expert`];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && draftValue.trim()) {
      e.preventDefault();
      onAddSkill?.();
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = skills.findIndex((s) => s.id === active.id);
    const newIndex = skills.findIndex((s) => s.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedSkills = arrayMove(skills, oldIndex, newIndex);
    onReorderSkills?.(reorderedSkills);
  };

  return (
    <div className="flex flex-col gap-3">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToParentElement]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={skills.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <div className="flex flex-wrap gap-2">
            {skills.map((skill) => (
              <SortableSkill
                key={skill.id}
                skill={skill}
                levels={levels}
                showLevels={showLevels}
                onChangeLevel={onChangeLevel}
                onRemoveSkill={onRemoveSkill}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Inline add */}
      <div className="flex items-center gap-2">
        <input
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          value={draftValue}
          placeholder={placeholder ?? t`Add a skill and press Enter`}
          aria-label={t`Add a skill`}
          onChange={(e) => {
            onDraftChange(e.target.value);
          }}
          onKeyDown={handleKeyDown}
        />
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0"
          disabled={!draftValue.trim()}
          aria-label={t`Add`}
          onClick={onAddSkill}
        >
          <PlusIcon size={16} />
        </Button>
      </div>
    </div>
  );
};
